import { faClock } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { PICKLEBALL_PLACEHOLDER_IMAGE } from '@pickleballinc/ui/components/global/utils/constants';
import { getRelativeTime } from '@pickleballinc/ui/components/global/utils/getRelativeTime';
import clsx from 'clsx';

import styles from './ExtraSmallNewsCard.module.css';

export interface ExtraSmallNewsCardProps {
	description?: string;
	image?: string;
	imageAltText?: string;
	publishDateDisplayed?: string | Date;
	title: string;
	author?: string;
	oneLineForDescription?: boolean;
	withShadow?: boolean;
	url?: string;
}

const ExtraSmallNewsCard = ({
	description,
	image,
	imageAltText,
	publishDateDisplayed,
	title,
	author,
	oneLineForDescription,
	withShadow,
	url
}: ExtraSmallNewsCardProps) => {
	const wrapperClasses = clsx(styles.wrapper, {
		[styles['wrapper--with-shadow'] as string]: withShadow
	});
	const imageClasses = clsx(styles.image);
	const contentClasses = clsx(styles.content, {
		[styles['content--with-shadow'] as string]: withShadow
	});
	const titleClasses = clsx(styles.title);
	const descriptionClasses = clsx(styles.description, {
		[styles['description--one-liner'] as string]: oneLineForDescription
	});
	const iconTimeClasses = clsx(styles.icon__time);
	const iconClasses = clsx(styles.icon);
	const timeClasses = clsx(styles.time);

	// Format the published date
	const formattedPublishDate =
		publishDateDisplayed instanceof Date ? publishDateDisplayed : publishDateDisplayed ? new Date(publishDateDisplayed) : null;

	const relativeTime = formattedPublishDate ? getRelativeTime(formattedPublishDate) : '';

	return (
		<div className={wrapperClasses} itemScope itemType="https://schema.org/NewsArticle">
			{url ? <meta itemProp="url" content={url} /> : null}
			{formattedPublishDate ? <meta itemProp="datePublished" content={formattedPublishDate.toISOString()} /> : null}
			<img
				className={imageClasses}
				style={{ objectFit: 'cover' }}
				alt={imageAltText || title}
				src={image || `${getCDNURL(PICKLEBALL_PLACEHOLDER_IMAGE, 276, 276)}`}
				itemProp="thumbnail"
			/>
			<div className={contentClasses}>
				<h3 className={titleClasses} itemProp="headline">
					{title}
				</h3>
				{(!oneLineForDescription && description) || oneLineForDescription ? (
					<p className={descriptionClasses} itemProp="accessibilitySummary">
						{description}
					</p>
				) : null}
				{relativeTime ? (
					<div className={iconTimeClasses}>
						<FontAwesomeIcon icon={faClock} size="sm" className={iconClasses} />
						<p className={timeClasses}>{relativeTime}</p>
						{author ? (
							<>
								<span>{` - `}</span>
								<p className={timeClasses} itemScope itemProp="author" itemType="https://schema.org/Person">
									<span itemProp="name">{author}</span>
								</p>
							</>
						) : null}
					</div>
				) : null}
			</div>
		</div>
	);
};

export default ExtraSmallNewsCard;
